// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Component, ChangeDetectionStrategy, computed, input } from '@angular/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { ShortNumberPipe } from '@lfx/shared/pipes/short-number.pipe';

@Component({
  selector: 'lfx-bar-echarts',
  templateUrl: './bar-echarts.component.html',
  styleUrls: ['./bar-echarts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgxEchartsModule],
  host: {
    '[class.sparkline-mode]': 'sparkline()',
    '[style.width]': '"100%"',
    '[style.height]': '"100%"',
    '[style.display]': '"block"',
  }
})
export class BarEchartsComponent {
  // Modern input signals
  readonly sparkline = input<boolean>(false); // Enable sparkline mode
  readonly sparklineData = input<number[]>([]);
  readonly seriesData = input<{
    data: number[];
    color?: string;
    label?: string;
    barWidth?: number;
    borderRadius?: number[];
  }[]>([]); // Multiple data series configuration for regular charts
  readonly stacked = input<boolean>(true); // Enable stacked bar chart
  readonly hideLegend = input<boolean>(false); // Hide legend
  readonly xAxisLabels = input<string[]>([]);

  // Modern computed signal for chart options
  protected chartOption = computed(() => {
    if (this.sparkline()) {
      // Use sparkline configuration with sparklineData
      return this.getSparklineConfig();
    } else {
      // Use regular chart configuration with seriesData
      return this.getRegularBarConfig();
    }
  });

  private shortNumberPipe = new ShortNumberPipe();

  /**
   * Get sparkline data - either from explicit sparklineData input or derive from seriesData
   */
  private getSparklineData(): number[] {
    // If sparklineData is explicitly provided, use it
    if (this.sparklineData().length > 0) {
      return this.sparklineData();
    }

    // Otherwise, derive from first series in seriesData
    const seriesData = this.seriesData();
    if (seriesData.length > 0 && seriesData[0].data) {
      return seriesData[0].data;
    }

    // Fallback to empty array
    return [];
  }

  private getSparklineConfig(): object {
    const data = this.getSparklineData();

    return {
      animation: false,
      grid: {
        left: 0,
        right: 0,
        top: 8,
        bottom: 8,
        containLabel: false
      },
      xAxis: {
        type: 'category',
        show: false,
        data: data.map((_, index) => index),
        boundaryGap: true, // Bars need boundary gap
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [{
        type: 'bar',
        data: data,
        itemStyle: {
          color: '#009AFF',
          borderRadius: [2, 2, 2, 2] // Rounded corners
        },
        barWidth: 4, // Fixed bar width in pixels
        barGap: '16px' // Fixed gap between bars in pixels
      }],
      tooltip: {
        show: false
      }
    };
  }

  private getRegularBarConfig(): object {
    // Validate seriesData
    const seriesData = this.seriesData();
    if (seriesData.length === 0) {
      return {};
    }

    // Get max data length with fallback
    const dataLengths = seriesData.map(s => s.data?.length || 0);
    const maxDataLength = Math.max(...dataLengths, 0);

    if (maxDataLength === 0) {
      return {};
    }

    const defaultBarOption = {
      grid: {
        top: '10%',
        bottom: '14%',
        right: '5%',
        left: '10%'
      },
      xAxis: {
        type: 'category',
        axisLine: {
          lineStyle: {
            color: '#E5E7EB'
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#6B7280',
          fontSize: 12,
          fontFamily: 'Inter'
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#6B7280',
          fontSize: 12,
          fontFamily: 'Inter',
          formatter: (value: number) => this.shortNumberPipe.transform(value)
        },
        splitLine: {
          lineStyle: {
            color: '#F3F4F6',
            type: 'dashed'
          }
        }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#FFFFFF',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        textStyle: {
          color: '#374151',
          fontFamily: 'Inter'
        }
      }
    };

    const xAxisLabels = this.xAxisLabels();
    const xAxisData = xAxisLabels.length > 0
      ? xAxisLabels
      : Array.from({ length: maxDataLength }, (_, index) => `Category ${index + 1}`);

    return {
      animation: true,
      ...defaultBarOption,
      xAxis: {
        ...defaultBarOption.xAxis,
        data: xAxisData,
        boundaryGap: true
      },
      series: seriesData
        .filter(series => series.data && series.data.length > 0)
        .map((series, index) => ({
          type: 'bar',
          name: series.label || `Series ${index + 1}`,
          data: series.data,
          stack: this.stacked() ? 'stack' : undefined, // Use 'stack' as in the demo
          barMaxWidth: 32,
          barGap: '30%',
          barCategoryGap: '60%',
          itemStyle: {
            color: series.color || this.getLFXColor(index),
            borderRadius: [2, 2, 2, 2], // Use exact demo border radius
            borderWidth: 1,
            borderColor: this.stacked() ? '#fff' : undefined
          }
        })),
      legend: seriesData.length > 1 && !this.hideLegend() ? {
        top: 10,
        textStyle: {
          color: '#374151',
          fontFamily: 'Inter',
          fontSize: 12
        }
      } : undefined
    };
  }

  /**
   * Get LFX brand colors for series by index (from demo)
   */
  private getLFXColor(index: number): string {
    // LFX Insights Brand Colors (from actual codebase)
    const lfxColors = [
      '#0094FF', // brand.500 (primary blue)
      '#64748B', // neutral.500 (gray)
    ];
    return lfxColors[index % lfxColors.length];
  }

  /**
   * Get default color for series by index (legacy method)
   */
  private getDefaultColor(index: number): string {
    return this.getLFXColor(index);
  }
}
